import { comuneByIstat } from '@sse/gazetteer';
import { slugify } from '@sse/gazetteer/normalize';
import { BedDouble, Download, ExternalLink, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { renderSVG } from 'uqr';
import { ComunePicker } from '../components/comune-picker';
import {
  CustomPageCard,
  TranslationStatus,
  EMPTY_CUSTOM,
  SectionsCard,
  type GuestCustom,
  type GuestSections,
} from '../components/guest-custom-editor';
import {
  ButtonsCard,
  WelcomeCard,
  buttonProblem,
  cleanButton,
  type GuestBanner,
  type GuestButton,
} from '../components/guest-intro-editor';
import { Card, Field, btnPrimary, btnSecondary, inputCls } from '../components/ui';
import { ApiError } from '../lib/api';
import { formatStamp } from '../lib/format';
import { useSession } from '../lib/session';

/** Struttura con la pagina per gli ospiti (API `/admin/guests`). */
interface Guest {
  slug: string;
  name: string;
  comuneId: string;
  lat: number;
  lng: number;
  radiusKm: number;
  enabled: boolean;
  note?: string;
  sections?: GuestSections;
  welcome?: string;
  banner?: GuestBanner;
  buttons?: GuestButton[];
  custom?: GuestCustom;
  customI18n?: Record<string, unknown> & { at?: string };
  version: number;
  updatedAt: string;
  updatedBy: string;
}

/** Indirizzo pubblico della pagina; il QR aggiunge la provenienza per le statistiche. */
const pageUrl = (site: string, slug: string) => `${site.replace(/\/$/, '')}/guests/${slug}`;
const qrUrl = (site: string, slug: string) => `${pageUrl(site, slug)}?ref=qr-${slug}`;

/**
 * Strutture (solo admin, docs/07-admin-e-bot.md "Strutture"): strutture ricettive con la pagina
 * per i loro ospiti, `/guests/<slug>` sul sito. Fuori dal menu, dalla sitemap e da Google: ci si
 * arriva dal link o dal QR del cartoncino.
 */
export function GuestsList() {
  const { api, isAdmin, config } = useSession();
  const [rows, setRows] = useState<Guest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!isAdmin) return;
    api<{ guests: Guest[] }>('/guests')
      .then((r) => setRows(r.guests))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [api, isAdmin]);
  if (!isAdmin)
    return <main className="p-6 text-muted">Le strutture sono riservate agli admin.</main>;
  return (
    <main className="mx-auto max-w-[1100px] p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-h2">Strutture</h1>
        <Link to="/guests/new" className={btnPrimary}>
          <Plus className="size-4" aria-hidden /> Nuova struttura
        </Link>
      </div>
      <p className="mt-1 max-w-[70ch] text-sm text-muted">
        Ogni struttura ha una pagina per i suoi ospiti con gli eventi vicini e quelli da non perdere
        dei prossimi 7 giorni. La pagina non è nel menu del sito, né nella sitemap, né su Google: ci
        si arriva solo dal link o dal QR del cartoncino.
      </p>
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {!rows ? (
        <p className="mt-4 text-sm text-muted">Caricamento…</p>
      ) : rows.length === 0 ? (
        <div className="mt-6 flex flex-col items-start gap-3 border border-surface-strong p-6">
          <BedDouble className="size-8 text-muted" aria-hidden />
          <p>Nessuna struttura: aggiungi la prima per creare la sua pagina e il cartoncino.</p>
        </div>
      ) : (
        <ul className="mt-6 flex flex-col divide-y divide-surface-strong border-y border-surface-strong">
          {rows.map((g) => (
            <li key={g.slug}>
              <Link
                to={`/guests/${g.slug}`}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 py-3 hover:bg-surface"
              >
                <span className="min-w-0 flex-1 font-medium">{g.name}</span>
                <span
                  className={`px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.04em] ${g.enabled ? 'bg-ok-soft' : 'bg-surface text-muted'}`}
                >
                  {g.enabled ? 'Attiva' : 'Spenta'}
                </span>
                <span className="text-sm text-muted">
                  {comuneByIstat(g.comuneId)?.name} · entro {g.radiusKm} km ·{' '}
                  {pageUrl(config.siteUrl, g.slug).replace(/^https?:\/\//, '')}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

interface Form {
  slug: string;
  name: string;
  comuneId: string | undefined;
  lat: string;
  lng: string;
  radiusKm: string;
  enabled: boolean;
  note: string;
  address: string;
  sections: GuestSections;
  welcome: string;
  banner: GuestBanner | null;
  buttons: GuestButton[];
  custom: GuestCustom;
}

const EMPTY: Form = {
  slug: '',
  name: '',
  comuneId: undefined,
  lat: '',
  lng: '',
  radiusKm: '20',
  enabled: true,
  note: '',
  address: '',
  sections: {},
  welcome: '',
  banner: null,
  buttons: [],
  custom: EMPTY_CUSTOM,
};

/** Scheda della struttura: dati, posizione, raggio, link e cartoncino con il QR. */
export function GuestEditor() {
  const { api, isAdmin, config } = useSession();
  const { slug: param } = useParams();
  const isNew = !param || param === 'new';
  const navigate = useNavigate();
  const [form, setForm] = useState<Form>(EMPTY);
  const [current, setCurrent] = useState<Guest | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew || !isAdmin) return;
    api<{ guest: Guest }>(`/guests/${param}`)
      .then(({ guest }) => {
        setCurrent(guest);
        setForm({
          slug: guest.slug,
          name: guest.name,
          comuneId: guest.comuneId,
          lat: String(guest.lat),
          lng: String(guest.lng),
          radiusKm: String(guest.radiusKm),
          enabled: guest.enabled,
          note: guest.note ?? '',
          address: '',
          sections: guest.sections ?? {},
          welcome: guest.welcome ?? '',
          banner: guest.banner ?? null,
          buttons: guest.buttons ?? [],
          custom: { ...EMPTY_CUSTOM, ...guest.custom },
        });
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [api, isAdmin, isNew, param]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const slug = isNew && !slugTouched ? slugify(form.name) : form.slug;
  const site = config.siteUrl;
  const qrSvg = useMemo(
    () =>
      !isNew && current
        ? `data:image/svg+xml;utf8,${encodeURIComponent(renderSVG(qrUrl(site, current.slug), { border: 2 }))}`
        : null,
    [isNew, current, site],
  );

  if (!isAdmin)
    return <main className="p-6 text-muted">Le strutture sono riservate agli admin.</main>;

  const lat = Number(form.lat);
  const lng = Number(form.lng);
  const hasPos = form.lat !== '' && form.lng !== '' && Number.isFinite(lat) && Number.isFinite(lng);

  const useCentre = () => {
    const c = form.comuneId ? comuneByIstat(form.comuneId) : undefined;
    if (!c) return;
    setForm((f) => ({ ...f, lat: String(c.centroid.lat), lng: String(c.centroid.lng) }));
    setMsg(`Posizione: centro di ${c.name}.`);
  };

  const locate = async () => {
    if (!form.comuneId) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api<{ lat: number; lng: number; label: string }>('/events/geocode', {
        method: 'POST',
        body: {
          comuneId: form.comuneId,
          ...(form.name.trim() ? { venueName: form.name.trim() } : {}),
          ...(form.address.trim() ? { address: form.address.trim() } : {}),
        },
      });
      setForm((f) => ({ ...f, lat: String(r.lat), lng: String(r.lng) }));
      setMsg(`Trovato: ${r.label}`);
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const { guest } = await api<{ guest: Guest }>(`/guests/${slug}`, {
        method: 'PUT',
        body: {
          name: form.name.trim(),
          comuneId: form.comuneId,
          lat,
          lng,
          radiusKm: Number(form.radiusKm),
          enabled: form.enabled,
          ...(form.note.trim() ? { note: form.note.trim() } : {}),
          sections: form.sections,
          ...(form.welcome.trim() ? { welcome: form.welcome.trim() } : {}),
          ...(form.banner ? { banner: form.banner } : {}),
          buttons: form.buttons.map(cleanButton),
          ...(hasCustom ? { custom: form.custom } : {}),
          ...(current ? { version: current.version } : {}),
        },
      });
      setCurrent(guest);
      setForm((f) => ({
        ...f,
        welcome: guest.welcome ?? '',
        buttons: guest.buttons ?? [],
        custom: { ...EMPTY_CUSTOM, ...guest.custom },
      }));
      setMsg('Salvato. La pagina sul sito si aggiorna entro un minuto.');
      if (isNew) navigate(`/guests/${guest.slug}`, { replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        const issues = e.body.issues?.map((i) => `${i.path}: ${i.message}`).join('; ');
        setError(issues ? `${e.message} (${issues})` : e.message);
      } else setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!current) return;
    if (
      !window.confirm(
        `Eliminare "${current.name}"? La sua pagina sul sito risponderà "non trovata".`,
      )
    )
      return;
    await api(`/guests/${current.slug}`, { method: 'DELETE' });
    navigate('/guests');
  };

  const card = async () => {
    if (!current) return;
    // pdf-lib solo quando serve (non pesa sul resto del pannello)
    const { downloadPdf, guestCardPdf } = await import('../lib/guest-card');
    const bytes = await guestCardPdf({
      url: qrUrl(site, current.slug),
      name: current.name,
      display: pageUrl(site, current.slug).replace(/^https?:\/\//, ''),
      // la quarta voce del cartoncino segue i tasti visibili della struttura
      wifi: (current.buttons ?? []).some((b) => b.kind === 'wifi' && !b.hidden),
      info: (current.buttons ?? []).some((b) => !b.hidden) || !!current.custom?.body,
    });
    downloadPdf(bytes, `cartoncino-${current.slug}.pdf`);
  };

  // scheda della struttura: si salva se c'è qualcosa (il nome della scheda è facoltativo)
  const hasCustom = !!(
    form.custom.tab.trim() ||
    form.custom.title.trim() ||
    form.custom.body.trim()
  );
  const buttonsOk = form.buttons.every((b) => !buttonProblem(b));
  // testi per gli ospiti uguali a quelli salvati: lo stato della traduzione vale per loro
  const textsOf = (g: {
    welcome?: string;
    buttons?: GuestButton[];
    custom?: Partial<GuestCustom>;
  }) =>
    JSON.stringify([
      (g.welcome ?? '').trim(),
      [g.custom?.tab ?? '', g.custom?.title ?? '', g.custom?.body ?? ''].map((x) => x.trim()),
      (g.buttons ?? []).map((b) =>
        [b.id, b.label, b.value, b.popupTitle ?? '', b.popupText ?? ''].map((x) => x.trim()),
      ),
    ]);
  const sameTexts = !!current && textsOf(current) === textsOf(form);
  const hasTexts = !!(form.welcome.trim() || form.buttons.length || form.custom.body.trim());
  const valid =
    form.name.trim().length >= 2 && !!form.comuneId && hasPos && slug.length > 0 && buttonsOk;

  return (
    <main className="mx-auto max-w-[900px] p-4 md:p-6">
      <p className="text-sm">
        <Link to="/guests" className="underline">
          Strutture
        </Link>
      </p>
      <h1 className="mt-1 font-display text-h2">{isNew ? 'Nuova struttura' : form.name}</h1>
      {current && (
        <p className="mt-1 text-sm text-muted">
          Modificata {formatStamp(current.updatedAt)} da {current.updatedBy}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          {error}
        </p>
      )}
      <div className="mt-6 flex flex-col gap-6">
        <Card title="Struttura">
          <Field label="Nome" hint="Compare in cima alla pagina: «Per gli ospiti di …».">
            <input
              className={inputCls}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
            />
          </Field>
          <Field
            label="Indirizzo della pagina"
            hint={
              isNew
                ? 'Lettere minuscole, numeri e trattini. Non si cambia dopo la creazione.'
                : 'Non si cambia: è nel QR già stampato.'
            }
          >
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted">/guests/</span>
              <input
                className={inputCls}
                value={slug}
                disabled={!isNew}
                onChange={(e) => {
                  setSlugTouched(true);
                  set('slug', slugify(e.target.value));
                }}
              />
            </div>
          </Field>
          <Field label="Comune">
            <ComunePicker value={form.comuneId} onChange={(v) => set('comuneId', v)} />
          </Field>
          <Field label="Nota interna" hint="Referente, accordi. Mai sul sito.">
            <textarea
              className={`${inputCls} h-20 py-2`}
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => set('enabled', e.target.checked)}
            />
            Attiva (spenta: la pagina risponde «non trovata»)
          </label>
        </Card>

        <Card title="Posizione e raggio">
          <Field
            label="Indirizzo (facoltativo)"
            hint="Per «Trova dall'indirizzo»: via e numero, o località."
          >
            <input
              className={inputCls}
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={btnSecondary}
              disabled={!form.comuneId || busy}
              onClick={locate}
            >
              Trova dall'indirizzo
            </button>
            <button
              type="button"
              className={btnSecondary}
              disabled={!form.comuneId}
              onClick={useCentre}
            >
              Usa il centro del comune
            </button>
            {hasPos && (
              <a
                className={btnSecondary}
                href={`https://www.google.com/maps?q=${lat},${lng}`}
                target="_blank"
                rel="noopener"
              >
                <ExternalLink className="size-4" aria-hidden /> Controlla sulla mappa
              </a>
            )}
          </div>
          {msg && <p className="text-sm">{msg}</p>}
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Latitudine">
              <input
                className={inputCls}
                inputMode="decimal"
                value={form.lat}
                onChange={(e) => set('lat', e.target.value)}
              />
            </Field>
            <Field label="Longitudine">
              <input
                className={inputCls}
                inputMode="decimal"
                value={form.lng}
                onChange={(e) => set('lng', e.target.value)}
              />
            </Field>
            <Field label="Raggio «vicino a te» (km)">
              <input
                className={inputCls}
                type="number"
                min={5}
                max={60}
                value={form.radiusKm}
                onChange={(e) => set('radiusKm', e.target.value)}
              />
            </Field>
          </div>
        </Card>

        <SectionsCard value={form.sections} onChange={(v) => set('sections', v)} />

        <WelcomeCard
          slug={current?.slug ?? null}
          welcome={form.welcome}
          onWelcome={(v) => set('welcome', v)}
          banner={form.banner}
          onBanner={(v) => set('banner', v)}
          bannerVisible={form.sections.banner !== false}
          onBannerVisible={(v) => set('sections', { ...form.sections, banner: v })}
        />

        <ButtonsCard
          buttons={form.buttons}
          onChange={(v) => set('buttons', v)}
          visible={form.sections.buttons !== false}
          onVisible={(v) => set('sections', { ...form.sections, buttons: v })}
        />

        <CustomPageCard
          slug={current?.slug ?? null}
          value={form.custom}
          onChange={(v) => set('custom', v)}
          visible={form.sections.custom !== false}
          onVisible={(v) => set('sections', { ...form.sections, custom: v })}
        />

        {current && hasTexts && (
          <TranslationStatus
            slug={current.slug}
            {...(current.customI18n ? { i18n: current.customI18n } : {})}
            saved={sameTexts}
          />
        )}
        {!buttonsOk && (
          <p className="text-sm font-medium text-danger">
            Completa i tasti segnati in rosso prima di salvare.
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <button type="button" className={btnPrimary} disabled={!valid || busy} onClick={save}>
            {isNew ? 'Crea la struttura' : 'Salva'}
          </button>
          {current && (
            <button type="button" className={btnSecondary} onClick={remove}>
              Elimina
            </button>
          )}
        </div>

        {current && (
          <Card title="Link e cartoncino">
            <p className="text-sm">
              Pagina per gli ospiti:{' '}
              <a
                href={pageUrl(site, current.slug)}
                target="_blank"
                rel="noopener"
                className="font-medium underline"
              >
                {pageUrl(site, current.slug)}
              </a>
            </p>
            <p className="text-sm text-muted">
              Il QR porta a <code>{qrUrl(site, current.slug)}</code>: le visite arrivano con la
              provenienza <code>qr-{current.slug}</code> (Statistiche › Provenienza); gli eventi
              aperti da qui con <code>guests-{current.slug}</code>.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              {qrSvg && (
                <img
                  src={qrSvg}
                  alt="QR della pagina"
                  width={140}
                  height={140}
                  className="border border-surface-strong"
                />
              )}
              <button type="button" className={btnPrimary} onClick={card}>
                <Download className="size-4" aria-hidden /> Scarica il cartoncino (PDF A6)
              </button>
            </div>
          </Card>
        )}
      </div>
    </main>
  );
}
